import {Link, useLocation, useNavigate} from "react-router-dom";
import { useCurrentWidth } from "@hooks/useCurrentWidth";
import { MediaBreakPoints } from "@resources/constants";
import SubMenu from "../SubMenu/SubMenuAbout";
import { IMainMenuItemProps } from "./types";
import styles from "./MainMenuItem.module.scss";
import { useAppDispatch } from "@redux/store";
import { openFeedbackModal, closeFeedbackModal } from "@/redux/feedback/slice";
import arrowLeftGreenIcon from "@assets/icons/arrowCalendarLeftGreen.svg";

// Иконки подменю "Сервис"
import menuServicesIcon from "@assets/icons/menuServices.svg";
import menuProductsIcon from "@assets/icons/menuProducts.svg";
import menuRentIcon from "@assets/icons/menuRent.svg";
import menuTrainingIcon from "@assets/icons/menuTraining.svg";

// Иконки подменю "О клубе"
import menuGalleryIcon from "@assets/icons/menuGallery.svg";
import menuContactsIcon from "@assets/icons/menuContacts.svg";
import menuDirectionsIcon from "@assets/icons/menuDirections.svg";
import menuCourtLayoutIcon from "@assets/icons/menuCourtLayout.svg";
import menuNewsIcon from "@assets/icons/menuNews.svg";
import menuFeedbackIcon from "@assets/icons/menuFeedback.svg";
import menuRulesIcon from "@assets/icons/menuRules.svg";
import menuPrivacyIcon from "@assets/icons/menuPrivacy.svg";

// Иконки подменю "Padel"
import menuGamesIcon from "@assets/icons/menuGames.svg";
import menuPadelIcon from "@assets/icons/menuPadel.svg";

const subMenuItemsServices = [
  { id: "services", name: "Аренда ракеток", href: "/services/racket-calendar", icon: menuServicesIcon },
  { id: "products", name: "Товары", href: "/services/products", icon: menuProductsIcon },
  { id: "tennis-school", name: "Школа тенниса", href: "/services/tennis-school", icon: menuRentIcon },
  { id: "personal-trainings", name: "Персональные тренировки", href: "/services/personal-trainings", icon: menuTrainingIcon },
];

const subMenuItemsAbout = [
  { id: "foto", name: "Галерея", href: "/about/photo", icon: menuGalleryIcon },
  { id: "contacts", name: "Контакты", href: "/contacts", icon: menuContactsIcon },
  { id: "locationMap", name: "Схема проезда", href: "/about/direction", icon: menuDirectionsIcon },
  { id: "schemeCourts", name: "Схема кортов", href: "/about/court-layout", icon: menuCourtLayoutIcon },
  { id: "news", name: "Новости", href: "/about/news", icon: menuNewsIcon },
  { id: "feedback", name: "Обратная связь", href: "/feedback", icon: menuFeedbackIcon },
  { id: "rules", name: "Оферта и правила клуба", href: "/rules", icon: menuRulesIcon },
  { id: "privacy", name: "Политика конфиденциальности", href: "/privacy-policy", icon: menuPrivacyIcon },
];

const subMenuItemsPadel = [
  { id: "games", name: "Игры", href: "/padel/games/available", icon: menuGamesIcon },
  { id: "tournaments", name: "Турниры", href: "/padel/tournaments/available", icon: menuPadelIcon },
];

const subMenuItemsMap: Record<string, typeof subMenuItemsAbout> = {
  services: subMenuItemsServices,
  padel: subMenuItemsPadel,
  about: subMenuItemsAbout,
};

export const MainMenuItem = ({
  id,
  name,
  href,
  subMenu,
  icon,
  handleMainMenuMobileClose,
  activeSubMenu,
  onSubMenuOpen
}: IMainMenuItemProps) => {
  const windowWidth = useCurrentWidth();
  const breakPoint = MediaBreakPoints.DESKTOP;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = windowWidth < breakPoint;

  if (subMenu) {
    if (isMobile) {
      // Сервис — сразу переходим на страницу, без подменю
      if (id === "services") {
        if (activeSubMenu !== null) return null;
        return (
          <li key={id}>
            <Link to={href} onClick={handleMainMenuMobileClose}>
              <span className={styles.menuItemContent}>
                {icon && <img src={icon} alt="" className={styles.menuIcon} />}
                {name}
              </span>
            </Link>
          </li>
        );
      }

      // Padel — сразу переходим на лендинг /padel, без подменю
      if (id === "padel") {
        if (activeSubMenu !== null) return null;
        return (
          <li key={id}>
            <Link to={href} onClick={handleMainMenuMobileClose}>
              <span className={styles.menuItemContent}>
                {icon && <img src={icon} alt="" className={styles.menuIcon} />}
                {name}
              </span>
            </Link>
          </li>
        );
      }

      if (activeSubMenu === id) {
        const items = subMenuItemsMap[id] || subMenuItemsAbout;
        return (
          <>
            <li className={styles.subMenuBackItem}>
              <button
                className={styles.subMenuBackButton}
                onClick={() => onSubMenuOpen(null)}
              >
                <img src={arrowLeftGreenIcon} alt="back" className={styles.subMenuBackIcon} />
                {name}
              </button>
            </li>
            {items.map((item) => (
              <li key={item.id} className={styles.subMenuItem}>
                <Link to={item.href} onClick={handleMainMenuMobileClose}>
                  <span className={styles.menuItemContent}>
                    {item.icon && <img src={item.icon} alt="" className={styles.menuIcon} />}
                    {item.name}
                  </span>
                </Link>
              </li>
            ))}
          </>
        );
      }

      if (activeSubMenu !== null) {
        return null;
      }

      return (
        <li key={id}>
          <a onClick={() => onSubMenuOpen(id)} style={{ cursor: 'pointer' }}>
            <span className={styles.menuItemContent}>
              {icon && <img src={icon} alt="" className={styles.menuIcon} />}
              {name}
            </span>
          </a>
        </li>
      );
    } else {
      return (
        <li key={id}>
          <SubMenu name={name} href={href} type={id} />
        </li>
      );
    }
  } else {
    if (activeSubMenu !== null) {
      return null;
    }

    if (isMobile && (id === "contacts" || id === "school")) {
      return null;
    }

    return (
      <li key={id}>
        <Link
          onClick={()=> {href === location.pathname && handleMainMenuMobileClose()}}
          to={href}
        >
          <span className={styles.menuItemContent}>
            {icon && isMobile && <img src={icon} alt="" className={styles.menuIcon} />}
            {name}
          </span>
        </Link>
      </li>
    );
  }
};